import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker, Tag } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";

const MEASURES = [
  ["Engagement & attendance", "How consistently children and families choose to stay connected."],
  ["Participant-defined goals", "Progress against goals set by young people and families themselves."],
  ["Education engagement", "School participation, re-engagement and reduced suspension impacts."],
  ["Placement continuity", "Stability of placements and reduced breakdown pressure."],
  ["Family contact experiences", "How children and families experience contact, reconnection and reunification."],
  ["Cultural connection", "Strength of connection to culture, community, Country and identity."],
  ["Community participation", "Involvement in community programs, yarning circles and movement."],
  ["Service engagement", "Whether one relationship reduces fragmentation across services."],
  ["Feedback loops", "Participant, carer and family feedback shaping the model."],
  ["Qualitative narrative outcomes", "Stories of change and participant-created knowledge."],
];

const PLACEHOLDERS = [
  ["Pilot Data", "Coming Soon", "Once pilot programs are delivered with partners, data will be published here — honestly, including what didn't work."],
  ["Evaluation Partnership", "Expressions of Interest", "We are seeking a research and evaluation partner to co-design how impact is measured from day one."],
  ["Community Consultation Findings", "Future Publication", "What community tells us through consultation will be published back to community."],
  ["Annual Impact Snapshot", "Future Publication", "A yearly, plain-English snapshot of what Ngawilyi is learning and changing."],
];

const Impact = () => (
  <div data-testid="impact-page">
    <PageHero
      kicker="Impact & Evaluation"
      title={"Evidence, built honestly\nfrom day one."}
      lede="Ngawilyi is an emerging organisation — we won't manufacture evidence we don't have. Instead, here is how impact can be evaluated, and what we intend to measure when pilots begin."
    />

    <section className="px-6 py-14 sm:py-20 bg-cream">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-12">
          <Kicker>How impact can be evaluated</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal max-w-3xl">
            Measures that reflect <span className="italic text-terracotta">real lives, not just spreadsheets.</span>
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {MEASURES.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.05}>
              <div data-testid={`measure-${i}`} className="rounded-3xl bg-creamdark border border-ochre/25 p-7 h-full transition-transform duration-300 hover:-translate-y-1">
                <span className="font-display italic text-terracotta/60 text-lg">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-2 font-semibold text-charcoal">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-earth/80">{d}</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.5}>
            <div className="rounded-3xl bg-terracotta text-cream p-7 h-full flex flex-col justify-center">
              <p className="font-display italic text-2xl leading-snug">Evaluation shaped with community — never done to community.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="px-6 pb-16 sm:pb-24 bg-cream">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-12">
          <Kicker>What's coming</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">Published as it happens.</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {PLACEHOLDERS.map(([t, tag, d], i) => (
            <Reveal key={t} delay={i * 0.07}>
              <div data-testid={`impact-placeholder-${i}`} className={`rounded-[36px] p-9 h-full ${i === 1 ? "bg-charcoal text-cream" : "bg-blush/40 border border-blush text-charcoal"}`}>
                <Tag tone={i === 1 ? "ochre" : "terracotta"}>{tag}</Tag>
                <h3 className="mt-5 font-display text-3xl font-light">{t}</h3>
                <p className={`mt-3 text-sm leading-relaxed ${i === 1 ? "text-cream/75" : "text-earth/85"}`}>{d}</p>
                {i === 1 && (
                  <Link data-testid="impact-eoi-link" to="/consultation" className="group mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-ochre">
                    Express Interest <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CTABand
      title="Help us prove what an Aunty beside a child can change."
      text="We are seeking evaluation partners and pilot funders to build the evidence base properly — intended outcomes are clearly labelled, never claimed early."
      primary={{ to: "/partner", label: "Partner With Ngawilyi" }}
      secondary={{ to: "/consultation", label: "Discuss Evaluation" }}
    />
  </div>
);

export default Impact;
